import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { localDay } from '../kpi-grid/kpi-trend-math';
import { DateChangedEvent } from '../date-range-picker/date-range-picker.component';
import { DATE_RANGE_PANEL_POSITIONS } from '../date-range-picker/date-range-panel-positions';
import {
  DATE_RANGE_PRESETS,
  DateRangeChange,
  DateRangePreset,
  DateRangeSelection,
  DateRangeWindow,
  resolveDateRangePreset,
} from './date-range-presets';

/** Only picks the days; each page owns the selection and converts the days to its own request format. */
@Component({
  selector: 'app-date-range-menu',
  templateUrl: './date-range-menu.component.html',
  styleUrls: ['./date-range-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DateRangeMenuComponent {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  readonly selection = input.required<DateRangeSelection>();
  readonly presets = input<readonly DateRangePreset[]>(DATE_RANGE_PRESETS.map((option) => option.preset));
  readonly selectionChange = output<DateRangeChange>();

  protected readonly customStart = signal(new Date());
  protected readonly customEnd = signal(new Date());
  protected readonly customOpen = signal(false);

  private readonly trigger = viewChild.required<ElementRef<HTMLElement>>('trigger');
  private readonly customRangeTemplate = viewChild.required<TemplateRef<unknown>>('customRangeTemplate');
  // Rendered in place, the sticky page header's stacking context would keep the panel below the app chrome.
  private customRangeOverlay: OverlayRef | null = null;

  protected readonly options = computed(() => {
    const presets = this.presets();
    return DATE_RANGE_PRESETS.filter((option) => presets.includes(option.preset));
  });
  protected readonly customActive = computed(() => this.selection().preset === 'custom');
  protected readonly customLabel = computed(() => {
    const selection = this.selection();
    return selection.preset === 'custom' ? `${localDay(selection.start)} – ${localDay(selection.end)}` : null;
  });
  protected readonly presetLabelKey = computed(() => {
    const preset = this.selection().preset;
    return DATE_RANGE_PRESETS.find((option) => option.preset === preset)?.labelKey ?? '';
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.disposeCustomRange());
  }

  protected selectPreset(preset: DateRangePreset): void {
    this.selectionChange.emit({ selection: { preset }, ...resolveDateRangePreset(preset) });
  }

  protected openCustomRange(): void {
    this.disposeCustomRange();
    const { start, end } = this.selectedWindow();
    this.customStart.set(start);
    this.customEnd.set(end);

    const overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.trigger())
        .withPositions(DATE_RANGE_PANEL_POSITIONS)
        .withPush(true),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });
    merge(
      overlayRef.backdropClick(),
      overlayRef.keydownEvents().pipe(filter((event) => event.key === 'Escape')),
    ).subscribe(() => this.closeCustomRange());
    overlayRef.attach(new TemplatePortal(this.customRangeTemplate(), this.viewContainerRef));
    this.customRangeOverlay = overlayRef;
    this.customOpen.set(true);
  }

  protected onCustomStart(event: DateChangedEvent): void {
    this.customStart.set(event.date);
    if (event.period !== undefined) {
      this.customEnd.set(new Date());
    }
  }

  protected onCustomEnd(event: DateChangedEvent): void {
    this.customEnd.set(event.date);
  }

  // Applied once on close: a page that reloads on every picked day would load a half-picked range.
  private closeCustomRange(): void {
    this.disposeCustomRange();
    const start = this.customStart();
    const end = this.customEnd();
    const selected = this.selectedWindow();
    if (localDay(start) === localDay(selected.start) && localDay(end) === localDay(selected.end)) {
      return;
    }
    const window = start.getTime() > end.getTime() ? { start: end, end: start } : { start, end };
    this.selectionChange.emit({ selection: { preset: 'custom', ...window }, ...window });
  }

  private disposeCustomRange(): void {
    this.customRangeOverlay?.dispose();
    this.customRangeOverlay = null;
    this.customOpen.set(false);
  }

  private selectedWindow(): DateRangeWindow {
    const selection = this.selection();
    return selection.preset === 'custom'
      ? { start: selection.start, end: selection.end }
      : resolveDateRangePreset(selection.preset);
  }
}
